import React, { useState } from "react";
import { useNavigate } from "react-router-dom";



const InternationalReturnCard = ({ onwardResults = [], onSelect }) => {
  const [activeTab, setActiveTab] = useState("flight");
  const [showDetailsIndex, setShowDetailsIndex] = useState(null);
  const [showFareModal, setShowFareModal] = useState(false);
  const [selectedFareDetails, setSelectedFareDetails] = useState(null);

  // Flatten segments safely across different server shapes.
  // - If Segments is nested (array of arrays): Segments = [onwardArray, returnArray]
  // - If Segments is a flat array of segment objects: Segments = [seg1, seg2, ...]
  // - If Segments is a single object, return it as single-element array.
  const getFlatSegments = (flight) => {
    const segs = flight?.Segments;
    if (!segs) return [];
    if (Array.isArray(segs)) {
      // nested legs -> each entry is an array
      if (segs.some(Array.isArray)) {
        const onward = Array.isArray(segs[0]) ? segs[0] : [];
        const ret = Array.isArray(segs[1]) ? segs[1] : [];
        return [...(onward.flat ? onward.flat() : onward), ...(ret.flat ? ret.flat() : ret)];
      }
      // flat array of segments
      return segs.flat ? segs.flat() : segs.slice();
    }
    // single object
    return [segs];
  };
// Add this at the top inside your component
const navigate = useNavigate();

  // Compute a lightweight meta for display/sorting that mirrors the logic
  // used by `Flights.js` computeMeta/getDuration: prefer cached __meta if present.
  const getFlightMeta = (f) => {
    if (!f) return { price: 0, duration: 0, departure: 0, arrival: 0 };
    if (f.__meta) return f.__meta;

    const price = (f?.Fare?.PublishedFare) || (f?.Fare?.BaseFare) || 0;

    // Determine first and last segments across possibly-nested Segments
    const isNested = Array.isArray(f?.Segments) && f.Segments.some(Array.isArray);
    let firstSeg = null;
    let lastSeg = null;
    let duration = 0;

    if (isNested) {
      // overall first = first element of first leg array
      const firstLegArray = f.Segments[0] || [];
      const lastLegArray = f.Segments[f.Segments.length - 1] || [];
      firstSeg = (firstLegArray && firstLegArray[0]) || null;
      lastSeg = (lastLegArray && lastLegArray[lastLegArray.length - 1]) || null;

      const firstDur = Number(firstSeg?.Duration ?? firstSeg?.duration ?? 0) || 0;
      const lastAccum = Number(lastSeg?.AccumulatedDuration ?? lastSeg?.Duration ?? lastSeg?.duration ?? 0) || 0;
      const combined = firstDur + lastAccum;
      if (combined > 0) duration = combined;
      else if (lastAccum > 0) duration = lastAccum;
      else {
        // fallback: sum durations of last leg
        const lastLegFlat = Array.isArray(lastLegArray) ? lastLegArray.flat() : [];
        duration = lastLegFlat.reduce((acc, s) => acc + (s.Duration || s.duration || 0), 0) || 0;
      }
    } else {
      const flat = (Array.isArray(f?.Segments) ? f.Segments.flat() : []) || [];
      firstSeg = flat[0] || null;
      lastSeg = flat[flat.length - 1] || null;
      duration = flat.reduce((acc, s) => acc + (s.Duration || s.duration || 0), 0) || 0;
      // prefer accumulated if present on last segment
      const lastAccum = Number(lastSeg?.AccumulatedDuration ?? 0) || 0;
      if (lastAccum > 0) duration = lastAccum;
    }

    const departure = firstSeg ? new Date(firstSeg?.Origin?.DepTime).getTime() : 0;
    const arrival = lastSeg ? new Date(lastSeg?.Destination?.ArrTime).getTime() : 0;

    const meta = { price, duration, departure, arrival, key: f.ResultIndex ?? JSON.stringify({ price, departure, arrival }) };
    // Do not mutate original flight in parent scope - cache locally but safe to attach for short-lived UI.
    try { f.__meta = meta; } catch (e) {}
    return meta;
  };

  // Calculate total duration
  const getDuration = (segments) => {
    if (!segments?.length) return "0h 0m";
    const totalMinutes = segments.reduce((acc, seg) => acc + (seg.Duration || 0), 0);
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="international_flights">
      {onwardResults.length > 0 ? (
        onwardResults.map((flight, i) => {
          const fare = flight?.Fare || {};
          const segmentsFlat = getFlatSegments(flight);
          const meta = getFlightMeta(flight);
          const itemKey = meta?.key || `flight-${i}`;
          // Normalize segment groups so we always iterate over leg-arrays
          const segmentGroups = Array.isArray(flight?.Segments)
            ? (flight.Segments.some(Array.isArray) ? flight.Segments : [flight.Segments])
            : [];

          return (
            <div key={itemKey} className="flights_card">
              {/* Flight Summary Card */}
              <div className="card stylecard">
                {/* Loop over each segment array */}
                {segmentGroups.map((segmentArray, segIdx) => {
                  const group = Array.isArray(segmentArray) ? segmentArray : (segmentArray ? [segmentArray] : []);
                  if (!group || group.length === 0) return null;

                  const firstLeg = group[0];
                  const lastLeg = group[group.length - 1];
                  const totalDuration = group.reduce((acc, seg) => acc + (seg.Duration || 0), 0);

                  return (
                    <div className="divstyle" key={segIdx}>
                      <div className="flight_leg">
                        <div className="logo_side">
                          <img
                            className="popupflightlogo"
                            src={`/airlinesimages/${firstLeg.Airline?.AirlineCode}.gif`}
                            alt={`${firstLeg.Airline?.AirlineName} Logo`}
                            onError={(e) => { e.target.onerror = null; e.target.src = "/airlinesimages/default.gif"; }}
                          />
                          <div className="carft_det">
                            <p className="AirlineName">{firstLeg.Airline?.AirlineName}</p>
                            <p className="AirlineCode">{firstLeg.Airline?.AirlineCode} {firstLeg.Airline?.FlightNumber}</p>
                            <p className="Craft"><b>Aircraft:</b> {firstLeg.Craft}</p>
                          </div>
                        </div>

                        {/* Origin */}
                        <div>
                          <p className="CityName">{firstLeg.Origin?.Airport?.CityName} ({firstLeg.Origin?.Airport?.AirportCode})</p>
                          <p className="DepTime">{new Date(firstLeg.Origin?.DepTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</p>
                        </div>

                        {/* Duration */}
                        <div>
                          <p className="Duration">{`${Math.floor(totalDuration / 60)}h ${totalDuration % 60}m`}</p>
                          <p className="StopOver">{segmentArray.length === 1 ? 'Non Stop' : `${segmentArray.length - 1} Stop(s)`}</p>
                        </div>

                        {/* Destination */}
                        <div>
                          <p className="CityName">{lastLeg.Destination?.Airport?.CityName} ({lastLeg.Destination?.Airport?.AirportCode})</p>
                          <p className="DepTime">{new Date(lastLeg.Destination?.ArrTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Fare Summary (once per flight) */}
                <div className="fare-summary mt-2">
                  <p className='fare'>
                    ₹ {meta.price ? Math.round(meta.price).toLocaleString('en-IN') : "N/A"}
                  </p>
                  <p className='total'>Total Fare</p>
                  <p className='Refundable'>{flight.IsRefundable ? 'Refundable' : 'Non-Refundable'}</p>
                </div>

                {/* Action Buttons */}
                <div className="flight_buttons mt-2 flex gap-2">
                  <button
                    type="button"
                    className="btn view-details"
                    onClick={() => {
                      setShowDetailsIndex(itemKey);
                      setActiveTab("flight");
                    }}
                  >
                    View Details
                  </button>

                  <button
                    type="button"
                    className="btn select-fare"
                    onClick={() => {
                      setSelectedFareDetails(flight);
                      setShowFareModal(true);
                    }}
                  >
                    Select Fare
                  </button>
                </div>
              </div>

              {/* Flight Details Modal */}
              {showDetailsIndex === itemKey && (
                <div className="fare-modal-overlay" onClick={() => setShowDetailsIndex(null)}>
                  <div className="fare-modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close" onClick={() => setShowDetailsIndex(null)}>×</button>

                    <div className="flight_tabs_section">
                      <div className="tab_headers">
                        {["flight", "baggage", "cancellation", "fare"].map((tab) => (
                          <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={activeTab === tab ? "active" : ""}
                          >
                            {tab === "flight" ? "Flight Details" :
                              tab === "baggage" ? "Baggage" :
                                tab === "cancellation" ? "Cancellation" :
                                  "Fare Summary"}
                          </button>
                        ))}
                      </div>

                      <div className="tab_content">
                        {/* Flight Tab */}
                        {activeTab === "flight" && (
                          <div>
                            {/* Onward Journey */}
                            {flight?.Segments?.[0]?.length > 0 && (
                              <>
                                <h3 className="journey-heading">✈️ Onward Journey</h3>
                                {flight.Segments[0].map((leg, idx) => (
                                  <React.Fragment key={`onward-${idx}`}>
                                    {/* Flight Card */}
                                    <div className="card">
                                      <div className="logo_side">
                                        <img
                                          className="popupflightlogo"
                                          src={`/airlinesimages/${leg.Airline?.AirlineCode}.gif`}
                                          alt={`${leg.Airline?.AirlineName} Logo`}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "/airlinesimages/default.gif";
                                          }}
                                        />
                                        <div className="carft_det">
                                          <p className="AirlineName">{leg.Airline?.AirlineName}</p>
                                          <p className="AirlineCode">
                                            {leg.Airline?.AirlineCode} {leg.FlightNumber}
                                          </p>
                                          <p className="Craft">
                                            <b>Aircraft:</b> {leg.Craft}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Origin */}
                                      <div>
                                        <p className="CityName">
                                          {leg.Origin?.Airport?.CityName} ({leg.Origin?.Airport?.AirportCode}) -{" "}
                                          {leg.Origin?.Airport?.AirportName}
                                        </p>
                                        <p className="DepTime">
                                          {new Date(leg.Origin?.DepTime).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                          })}
                                          <br />
                                          <span style={{ fontSize: "13px" }}>
                                            {(() => {
                                              const d = new Date(leg.Origin?.DepTime);
                                              const day = String(d.getDate()).padStart(2, "0");
                                              const month = String(d.getMonth() + 1).padStart(2, "0"); // Month is 0-based
                                              const year = d.getFullYear();
                                              return `${day}/${month}/${year}`;
                                            })()}
                                          </span>

                                        </p>

                                      </div>

                                      {/* Duration + Stop Info */}
                                      <div>
                                        <p className="Duration">
                                          {`${Math.floor(leg.Duration / 60)}h ${leg.Duration % 60}m`}
                                        </p>
                                        <p className="StopOver">
                                          {leg.StopOver === false ? "Non Stop" : "Stop"}
                                        </p>
                                      </div>

                                      {/* Destination */}
                                      <div>
                                        <p className="CityName">
                                          {leg.Destination?.Airport?.CityName} ({leg.Destination?.Airport?.AirportCode}) -{" "}
                                          {leg.Destination?.Airport?.AirportName}
                                        </p>
                                        <p className="DepTime">
                                          {new Date(leg.Destination?.ArrTime).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                          })}
                                          <br />
                                          <span style={{ fontSize: "13px" }}>
                                            {(() => {
                                              const d = new Date(leg.Destination?.ArrTime);
                                              const day = String(d.getDate()).padStart(2, "0");
                                              const month = String(d.getMonth() + 1).padStart(2, "0"); // Month is 0-based
                                              const year = d.getFullYear();
                                              return `${day}/${month}/${year}`;
                                            })()}
                                          </span>

                                        </p>

                                      </div>
                                    </div>

                                    {/* Layover (only show if NOT last leg) */}
                                    {idx < flight.Segments[0].length - 1 && (
                                      <div className="layover-line">
                                        <span className="StopOver">
                                          ───────────── {leg.GroundTime > 0
                                            ? `Layover: ${Math.floor(leg.GroundTime / 60)}h ${leg.GroundTime % 60}m`
                                            : "No Layover"} ─────────────
                                        </span>
                                      </div>
                                    )}
                                  </React.Fragment>
                                ))}
                              </>
                            )}

                            {/* Return Journey */}
                            {flight?.Segments?.[1]?.length > 0 && (
                              <>
                                <h3 className="journey-heading">↩️ Return Journey</h3>
                                {flight.Segments[1].map((leg, idx) => (
                                  <React.Fragment key={`return-${idx}`}>
                                    {/* Flight Card */}
                                    <div className="card">
                                      <div className="logo_side">
                                        <img
                                          className="popupflightlogo"
                                          src={`/airlinesimages/${leg.Airline?.AirlineCode}.gif`}
                                          alt={`${leg.Airline?.AirlineName} Logo`}
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = "/airlinesimages/default.gif";
                                          }}
                                        />
                                        <div className="carft_det">
                                          <p className="AirlineName">{leg.Airline?.AirlineName}</p>
                                          <p className="AirlineCode">
                                            {leg.Airline?.AirlineCode} {leg.FlightNumber}
                                          </p>
                                          <p className="Craft">
                                            <b>Aircraft:</b> {leg.Craft}
                                          </p>
                                        </div>
                                      </div>

                                      {/* Origin */}
                                      <div>
                                        <p className="CityName">
                                          {leg.Origin?.Airport?.CityName} ({leg.Origin?.Airport?.AirportCode}) -{" "}
                                          {leg.Origin?.Airport?.AirportName}
                                        </p>
                                        <p className="DepTime">
                                          {new Date(leg.Origin?.DepTime).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                          })}
                                          <br />
                                          <span style={{ fontSize: "13px" }}>
                                            {(() => {
                                              const d = new Date(leg.Origin?.DepTime);
                                              const day = String(d.getDate()).padStart(2, "0");
                                              const month = String(d.getMonth() + 1).padStart(2, "0"); // Month is 0-based
                                              const year = d.getFullYear();
                                              return `${day}/${month}/${year}`;
                                            })()}
                                          </span>

                                        </p>
                                      </div>

                                      {/* Duration + Stop Info */}
                                      <div>
                                        <p className="Duration">
                                          {`${Math.floor(leg.Duration / 60)}h ${leg.Duration % 60}m`}
                                        </p>
                                        <p className="StopOver">
                                          {leg.StopOver === false ? "Non Stop" : "Stop"}
                                        </p>
                                      </div>

                                      {/* Destination */}
                                      <div>
                                        <p className="CityName">
                                          {leg.Destination?.Airport?.CityName} ({leg.Destination?.Airport?.AirportCode}) -{" "}
                                          {leg.Destination?.Airport?.AirportName}
                                        </p>
                                        <p className="DepTime">
                                          {new Date(leg.Destination?.ArrTime).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                          })}
                                          <br />
                                          <span style={{ fontSize: "13px" }}>
                                            {(() => {
                                              const d = new Date(leg.Destination?.ArrTime);
                                              const day = String(d.getDate()).padStart(2, "0");
                                              const month = String(d.getMonth() + 1).padStart(2, "0"); // Month is 0-based
                                              const year = d.getFullYear();
                                              return `${day}/${month}/${year}`;
                                            })()}
                                          </span>

                                        </p>
                                      </div>
                                    </div>

                                    {/* Layover (only show if NOT last leg) */}
                                    {idx < flight.Segments[1].length - 1 && (
                                      <div className="layover-line">
                                        <span className="StopOver">
                                          ───────────── {leg.GroundTime > 0
                                            ? `Layover: ${Math.floor(leg.GroundTime / 60)}h ${leg.GroundTime % 60}m`
                                            : "No Layover"} ─────────────
                                        </span>
                                      </div>
                                    )}
                                  </React.Fragment>
                                ))}
                              </>
                            )}
                          </div>
                        )}






                        {/* Baggage Tab */}
                        {activeTab === "baggage" && (
                          <div>
                            <table className="baggage-table w-full border-collapse">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="p-2 border">Airline</th>
                                  <th className="p-2 border">Sector</th>
                                  <th className="p-2 border">Check-in Baggage</th>
                                  <th className="p-2 border">Cabin Baggage</th>
                                </tr>
                              </thead>
                              <tbody>
                                {segmentsFlat.map((leg, idx) => (
                                  <tr key={idx} className="border-b">
                                    <td className="p-2 border flex items-center gap-2">
                                      <img
                                        src={`/airlinesimages/${leg.Airline?.AirlineCode}.gif`}
                                        alt={`${leg.Airline?.AirlineName} Logo`}
                                        className="w-6 h-6"
                                        onError={(e) => { e.target.onerror = null; e.target.src = '/airlinesimages/default.gif'; }}
                                      />
                                      {leg.Airline?.AirlineName || 'N/A'}
                                    </td>
                                    <td className="p-2 border">{leg.Origin?.Airport?.AirportCode || 'N/A'} - {leg.Destination?.Airport?.AirportCode || 'N/A'}</td>
                                    <td className="p-2 border">{leg.Baggage || 'N/A'}</td>
                                    <td className="p-2 border">{leg.CabinBaggage || 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Cancellation Tab */}
                        {activeTab === "cancellation" && (
                          <div>
                            <h4>Cancellation / Reissue Rules</h4>
                            {Array.isArray(flight?.MiniFareRules) && flight.MiniFareRules.length > 0 ? (
                              <div className="fare-table">
                                {flight.MiniFareRules.flat().map((rule, index) => {
                                  const journeyFrom = (Number(rule?.From) || 0) + 1;
                                  return (
                                    <p key={index}>
                                      {`${rule?.JourneyPoints || '-'} - ${journeyFrom} to ${rule?.To || '-'}: ${rule?.Unit || '-'} ${rule?.Type || '-'} — ${rule?.Details || '-'}`}
                                    </p>
                                  );
                                })}
                              </div>
                            ) : (
                              <p>No cancellation or reissue rules available.</p>
                            )}
                          </div>
                        )}

                        {/* Fare Tab */}
                        {activeTab === "fare" && (
                          <div>
                            <h4>Fare Summary</h4>
                            {flight?.Fare ? (
                              <table className="fare-table">
                                <thead>
                                  <tr>
                                    <th>Passenger Type</th>
                                    <th>Base Fare</th>
                                    <th>Tax</th>
                                    <th>Other Charges</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>Adult</td>
                                    <td>₹{flight.Fare.BaseFare?.toLocaleString('en-IN') || 0}</td>
                                    <td>₹{flight.Fare.Tax?.toLocaleString('en-IN') || 0}</td>
                                    <td>₹{flight.Fare.OtherCharges?.toLocaleString('en-IN') || 0}</td>
                                    <td>₹{Math.round((flight.Fare.BaseFare || 0) + (flight.Fare.Tax || 0) + (flight.Fare.OtherCharges || 0)).toLocaleString('en-IN')}</td>
                                  </tr>
                                </tbody>
                              </table>
                            ) : (<p>No fare breakdown available.</p>)}

                            <div className="fare-total" style={{ marginTop: '1rem' }}>
                              <strong>Total Fare:</strong> ₹{flight.Fare?.PublishedFare ? Math.round(flight.Fare.PublishedFare).toLocaleString('en-IN') : 0}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Fare Selection Modal */}
              {showFareModal && selectedFareDetails === flight && (
                <div className="fare-modal-overlay" onClick={() => setShowFareModal(false)}>
                  <div className="fare-modal-content" onClick={(e) => e.stopPropagation()}>
                    <button className="modal-close" onClick={() => setShowFareModal(false)}>×</button>

                    {/* Flight summary */}
                    {selectedFareDetails && selectedFareDetails.Segments?.length > 0 && (
                      (() => {
                        const allSegments = selectedFareDetails.Segments.flat();
                        const firstSegment = allSegments[0];
                        const lastSegment = allSegments[allSegments.length - 1];

                        return (
                          <div className="flight-summary-bar">
                            <div className="logo-section">
                              <img
                                src={`/airlinesimages/${firstSegment.Airline?.AirlineCode}.gif`}
                                alt={`${firstSegment.Airline?.AirlineName} Logo`}
                                className="airline-logo"
                                onError={(e) => { e.target.onerror = null; e.target.src = '/airlinesimages/default.gif'; }}
                              />
                              <div className="airline-text">
                                <strong>{firstSegment.Airline?.AirlineName}</strong>
                                <span className="route">
                                  {firstSegment.Origin?.Airport?.CityName} → {lastSegment.Destination?.Airport?.CityName}
                                </span>
                                <span className="timing">
                                  {new Date(firstSegment.Origin?.DepTime).toLocaleDateString('en-GB', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: 'short',
                                    year: '2-digit'
                                  })}{' '}
                                  | Departure at {new Date(firstSegment.Origin?.DepTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}{' '}
                                  | Arrival at {new Date(lastSegment.Destination?.ArrTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })()
                    )}

                    {/* Fare Cards */}
                    <div className="fare-cards">
                      {(selectedFareDetails?.FareOptions || [selectedFareDetails]).map((fare, idx) => {
                        const segmentsFlat = fare?.Segments?.flat() || [];

                        return (
                          <div className="fare-card" key={idx}>
                            <div className="fare-price">
                              ₹{fare?.Fare?.PublishedFare ? Math.round(fare.Fare.PublishedFare).toLocaleString("en-IN") : "N/A"}
                              <span className="per-adult">Per Adult</span>
                            </div>

                            <div className="fare-type">
                              {fare?.FareClassification?.Type || "Regular"}
                            </div>

                            <div className="fare-section">
                              <strong>Baggage</strong>
                              {segmentsFlat.map((seg, i) => (
                                <p key={`bag-${i}`}>
                                  {seg.Origin?.Airport?.CityName} → {seg.Destination?.Airport?.CityName}:
                                  {seg?.CabinBaggage || "N/A"} Cabin / {seg?.Baggage || "N/A"} Check-in
                                </p>
                              ))}
                            </div>

                            <div className="fare-section">
                              <strong>Seats & Meals</strong>
                              <p><b>Seat Charge:</b> {fare?.Fare?.TotalSeatCharges || "N/A"}</p>
                              <p><b>Meal Charge:</b> {fare?.Fare?.TotalMealCharges || "N/A"}</p>
                            </div>

                            <div className="fare-table">
                              <strong>Cancellation or Reissue</strong>
                              {Array.isArray(fare?.MiniFareRules) && fare.MiniFareRules.length > 0 ? (
                                fare.MiniFareRules.flat().map((rule, index) => {
                                  const journeyFrom = (Number(rule?.From) || 0) + 1;
                                  return (
                                    <p key={index}>
                                      {`${rule?.JourneyPoints ?? '0'}- ${journeyFrom} to ${rule?.To ?? '0'}: ${rule?.Unit ?? '0'} ${rule?.Type ?? '0'} — ${rule?.Details ?? '0'}`}
                                    </p>
                                  );
                                })
                              ) : (
                                <p>No cancellation or reissue rules available.</p>
                              )}
                            </div>

                           <button
  className="btn btn-primary"
  style={{ marginTop: '1rem' }}
  onClick={() => {
    // Navigate to booking page and pass flight details
    navigate("/book-flight", {
      state: {
        tripType: "round trip",
        selectedOnward: fare, // current onward+return fare combo
        
      },
    });
    setShowFareModal(false);
  }}
>
  Book Now
</button>


                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

              )}
            </div>
          );
        })
      ) : (
        <p>No international flights found.</p>
      )}
    </div>
  );
};

export default InternationalReturnCard;
